import { useCallback, useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Luxury_Sedan", "Premium_SUV", "Executive_Sprinter", "Motor_Coach"];
const STATUSES = ["available", "on_trip", "maintenance", "offline"];

const empty = {
  category: "Luxury_Sedan", make: "", model: "", year: 2025,
  license_plate: "", capacity_passengers: 3, capacity_luggage: 2, status: "available",
};

export default function AdminFleet() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null); // null | {mode, payload}

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/vehicles");
      setList(data);
    } catch (e) { setErr(formatApiError(e)); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/vehicles/${id}`, { status });
      refresh();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const softDelete = async (id) => {
    if (!confirm("Set this vehicle to OFFLINE? Existing bookings will keep referencing it.")) return;
    try { await api.delete(`/admin/vehicles/${id}`); refresh(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const submit = async () => {
    const { mode, payload, id } = modal;
    setErr("");
    try {
      if (mode === "create") await api.post("/admin/vehicles", payload);
      else await api.patch(`/admin/vehicles/${id}`, payload);
      setModal(null);
      refresh();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-fleet" className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-2">Fleet</div>
          <h1 className="font-serif text-3xl font-semibold tracking-tighter">Vehicle management</h1>
        </div>
        {isAdmin && (
          <button data-testid="admin-fleet-add-btn" onClick={() => setModal({ mode: "create", payload: { ...empty } })}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] bg-white text-black px-5 py-2.5">
            <Plus size={14} strokeWidth={1.5} /> Add Vehicle
          </button>
        )}
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      <div className="bg-[#121214]/60 border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-fleet-table">
          <thead className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
            <tr className="border-b border-white/10">
              <th className="text-left px-5 py-3">Vehicle</th>
              <th className="text-left px-5 py-3">Category</th>
              <th className="text-left px-5 py-3">Plate</th>
              <th className="text-left px-5 py-3">Capacity</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map(v => (
              <tr key={v.id} data-testid={`admin-fleet-row-${v.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3">{v.make} {v.model}<div className="text-[10px] text-[#8E9AA6]">{v.year}</div></td>
                <td className="px-5 py-3 font-light">{v.category.replace(/_/g, " ")}</td>
                <td className="px-5 py-3 font-mono text-xs">{v.license_plate}</td>
                <td className="px-5 py-3 font-light">{v.capacity_passengers}p / {v.capacity_luggage}l</td>
                <td className="px-5 py-3">
                  {isAdmin ? (
                    <select value={v.status} onChange={(e) => updateStatus(v.id, e.target.value)}
                      data-testid={`admin-fleet-status-${v.id}`}
                      className="bg-[#0B0B0C] border border-white/15 px-2 py-1 text-[11px] uppercase tracking-[0.2em]">
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">{v.status}</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  {isAdmin && (
                    <>
                      <button onClick={() => setModal({ mode: "edit", id: v.id, payload: { ...v } })}
                        data-testid={`admin-fleet-edit-${v.id}`}
                        className="text-[10px] uppercase tracking-[0.25em] text-white hover:text-[#8E9AA6] mr-3">Edit</button>
                      <button onClick={() => softDelete(v.id)}
                        data-testid={`admin-fleet-delete-${v.id}`}
                        className="text-[10px] uppercase tracking-[0.25em] text-red-400 hover:text-red-300">Offline</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div data-testid="admin-fleet-modal" className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0B0B0C] border border-white/15 w-full max-w-lg p-6">
            <div className="flex justify-between items-start mb-5">
              <h2 className="font-serif text-xl">{modal.mode === "create" ? "Add vehicle" : "Edit vehicle"}</h2>
              <button onClick={() => setModal(null)} className="text-[#8E9AA6] hover:text-white p-1" data-testid="admin-fleet-modal-close"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {["make","model"].map(f => (
                <Field key={f} label={f} value={modal.payload[f]} onChange={(v) => setModal({ ...modal, payload: { ...modal.payload, [f]: v } })} testid={`admin-fleet-modal-${f}`} />
              ))}
              <Field label="year" type="number" value={modal.payload.year} onChange={(v) => setModal({ ...modal, payload: { ...modal.payload, year: Number(v) } })} testid="admin-fleet-modal-year" />
              <Field label="license_plate" value={modal.payload.license_plate} onChange={(v) => setModal({ ...modal, payload: { ...modal.payload, license_plate: v } })} testid="admin-fleet-modal-plate" />
              <Field label="capacity_passengers" type="number" value={modal.payload.capacity_passengers} onChange={(v) => setModal({ ...modal, payload: { ...modal.payload, capacity_passengers: Number(v) } })} testid="admin-fleet-modal-pax" />
              <Field label="capacity_luggage" type="number" value={modal.payload.capacity_luggage} onChange={(v) => setModal({ ...modal, payload: { ...modal.payload, capacity_luggage: Number(v) } })} testid="admin-fleet-modal-luggage" />
              <Select label="category" value={modal.payload.category} options={CATEGORIES} onChange={(v) => setModal({ ...modal, payload: { ...modal.payload, category: v } })} testid="admin-fleet-modal-category" />
              <Select label="status" value={modal.payload.status} options={STATUSES} onChange={(v) => setModal({ ...modal, payload: { ...modal.payload, status: v } })} testid="admin-fleet-modal-status" />
            </div>
            <button onClick={submit} data-testid="admin-fleet-modal-save" className="w-full mt-6 bg-white text-black hover:bg-[#E6E6E6] px-6 py-3 text-xs uppercase tracking-[0.25em]">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1.5">{label.replace(/_/g, " ")}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#121214] border border-white/15 px-3 py-2 text-sm focus:border-white focus:outline-none" />
    </div>
  );
}
function Select({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#121214] border border-white/15 px-3 py-2 text-sm focus:border-white focus:outline-none">
        {options.map(o => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
      </select>
    </div>
  );
}
